import { useEffect, useMemo, useState } from "react"
import { Head, Link, router, usePage } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import type { PageProps } from "@/types/inertia"

type SectionOption = { key: string; label: string }

type UserDetail = {
  id: number
  email: string
  admin: boolean
  full_access: boolean
  visible_count: number
  allowed_sections: string[]
  timezone: string | null
  created_at: string
  updated_at: string
}

/** Stable fingerprint of the access form, so "Save" only enables on real changes. */
function fingerprint(admin: boolean, fullAccess: boolean, sections: string[]) {
  const seesEverything = admin || fullAccess
  return JSON.stringify([admin, seesEverything, seesEverything ? "ALL" : [...sections].sort()])
}

export default function AdminUserShow({
  user,
  sections,
}: {
  user: UserDetail
  sections: SectionOption[]
}) {
  const page = usePage<PageProps>()
  const isSelf = page.props.current_user?.id === user.id

  const [admin, setAdmin] = useState(user.admin)
  const [fullAccess, setFullAccess] = useState(user.full_access)
  const [selected, setSelected] = useState<string[]>(user.allowed_sections)
  const [saving, setSaving] = useState(false)

  // Re-sync to server truth after every save/navigation.
  useEffect(() => {
    setAdmin(user.admin)
    setFullAccess(user.full_access)
    setSelected(user.allowed_sections)
  }, [user])

  const baseline = useMemo(
    () => fingerprint(user.admin, user.full_access, user.allowed_sections),
    [user],
  )
  const dirty = fingerprint(admin, fullAccess, selected) !== baseline
  const seesEverything = admin || fullAccess

  function toggleSection(key: string, checked: boolean) {
    setSelected((prev) =>
      checked ? Array.from(new Set([...prev, key])) : prev.filter((k) => k !== key),
    )
  }

  function save() {
    router.patch(
      `/admin/users/${user.id}`,
      { admin, full_access: admin || fullAccess, sections: selected },
      {
        preserveScroll: true,
        onStart: () => setSaving(true),
        onFinish: () => setSaving(false),
      },
    )
  }

  function reset() {
    setAdmin(user.admin)
    setFullAccess(user.full_access)
    setSelected(user.allowed_sections)
  }

  return (
    <>
      <Head title={user.email}>
        <meta name="description" content={`Access settings for ${user.email}.`} />
        <meta property="og:title" content={user.email} />
        <meta property="og:description" content={`Access settings for ${user.email}.`} />
      </Head>
      <AdminShell>
        <div className="border-b border-hairline pb-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1>{user.email}</h1>
              <p className="mt-1">User #{user.id}</p>
            </div>
            <div className="flex items-center gap-2">
              <AccessBadge
                admin={user.admin}
                fullAccess={user.full_access}
                count={user.visible_count}
                total={sections.length}
              />
              <Link
                href="/admin/users"
                className="text-sm text-ink-muted no-underline hover:text-ink-display"
              >
                Back to users
              </Link>
            </div>
          </div>
        </div>

        {page.props.flash?.notice && (
          <div className="callout callout-accent mt-6 text-sm">{page.props.flash.notice}</div>
        )}
        {page.props.flash?.alert && (
          <div className="callout callout-danger mt-6 text-sm">{page.props.flash.alert}</div>
        )}

        {/* Account details */}
        <dl className="mt-6 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-[max-content_1fr]">
          <DetailRow label="Email" value={user.email} />
          <DetailRow label="Timezone" value={user.timezone ?? "—"} />
          <DetailRow label="Created" value={formatDateTime(user.created_at)} />
          <DetailRow label="Updated" value={formatDateTime(user.updated_at)} />
        </dl>

        {/* Access editor */}
        <section className="mt-8 rounded-md border border-hairline bg-page">
          <div className="border-b border-hairline px-5 py-4">
            <h2 className="text-sm font-semibold text-ink-display">Access</h2>
            <p className="mt-1 text-sm text-ink-muted">
              Control which parts of SONAR this person sees when they log in.
            </p>
          </div>

          <div className="px-5 py-4">
            {/* Administrator */}
            <label htmlFor="admin" className="flex items-start gap-3 font-normal text-ink-body">
              <Checkbox
                id="admin"
                className="mt-0.5"
                checked={admin}
                disabled={isSelf}
                onChange={(e) => setAdmin(e.target.checked)}
              />
              <span>
                <span className="block text-sm font-medium text-ink-display">Administrator</span>
                <span className="mt-0.5 block text-sm text-ink-muted">
                  Sees every section plus the admin area (users, access, data refresh).
                  {isSelf && " You can't change your own admin access."}
                </span>
              </span>
            </label>
          </div>

          <div className="border-t border-hairline px-5 py-4">
            <span className="block text-sm font-medium text-ink-display">Report sections</span>

            {/* Full access */}
            <label
              htmlFor="full_access"
              className="mt-3 flex items-start gap-3 font-normal text-ink-body"
            >
              <Checkbox
                id="full_access"
                className="mt-0.5"
                checked={seesEverything}
                disabled={admin}
                onChange={(e) => setFullAccess(e.target.checked)}
              />
              <span>
                <span className="block text-sm text-ink-display">
                  Full access — every report section
                </span>
                {admin && (
                  <span className="mt-0.5 block text-sm text-ink-muted">
                    Always on for administrators.
                  </span>
                )}
              </span>
            </label>

            {/* Per-section checklist */}
            <div className="mt-4">
              <p className="text-xs uppercase tracking-wide text-ink-muted">
                Or choose specific sections
              </p>
              <ul className="mt-2 space-y-2">
                {sections.map((section) => {
                  const checked = seesEverything || selected.includes(section.key)
                  return (
                    <li key={section.key}>
                      <label
                        htmlFor={`section-${section.key}`}
                        className="flex items-center gap-3 font-normal text-ink-body"
                      >
                        <Checkbox
                          id={`section-${section.key}`}
                          checked={checked}
                          disabled={seesEverything}
                          onChange={(e) => toggleSection(section.key, e.target.checked)}
                        />
                        <span className="text-sm">{section.label}</span>
                      </label>
                    </li>
                  )
                })}
              </ul>
              {!seesEverything && selected.length === 0 && (
                <p className="mt-3 text-sm text-ink-muted">
                  No sections selected — this person will see no reports until you tick at least
                  one (they can still sign in and reach their profile).
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-hairline px-5 py-4">
            {dirty && !saving && (
              <Button type="button" variant="ghost" size="sm" onClick={reset}>
                Cancel
              </Button>
            )}
            <Button type="button" onClick={save} disabled={!dirty || saving}>
              {saving ? "Saving…" : "Save access"}
            </Button>
          </div>
        </section>
      </AdminShell>
    </>
  )
}

function AccessBadge({
  admin,
  fullAccess,
  count,
  total,
}: {
  admin: boolean
  fullAccess: boolean
  count: number
  total: number
}) {
  if (admin) return <Badge tone="accent">Admin</Badge>
  if (fullAccess) return <Badge tone="accent">All sections</Badge>
  if (count === 0) return <Badge>No access</Badge>
  return (
    <Badge>
      {count} of {total} sections
    </Badge>
  )
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-sm text-ink-muted">{label}</dt>
      <dd className="text-sm text-ink-body">{value}</dd>
    </>
  )
}

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}
